import { regularApiClient } from "../utils/kiota-client";
import type { CoolingRegimePriorityDto } from "../../../RoyalFloraHolland.Aap.Services.Api.Web.Kiota/Typescript/Api/models/index";

export interface CoolingRegimePriorityListResult {
  value: CoolingRegimePriorityDto[];
  error?: string;
}

export interface CoolingRegimePriorityFilters {
  page: number;
  pageSize: number;
  priorityId?: string;
  description?: string;
  vbnChangeDate?: string;
  modifiedDate?: string;
  modifiedBy?: string;
}

export async function loadCoolingRegimePriorities(
  filters: CoolingRegimePriorityFilters,
): Promise<CoolingRegimePriorityListResult> {
  try {
    const queryConfig = {
      queryParameters: {
        top: filters.pageSize,
        skip: (filters.page - 1) * filters.pageSize,
        filter: buildFilterString(filters),
        orderby: "coolingRegimePriorityId asc",
      },
    };

    const response = await regularApiClient.api
      .vWithVersion("1")
      .coolingRegimePriorities.get(queryConfig);

    return {
      value: Array.isArray(response) ? response : [],
    };
  } catch (error) {
    return {
      value: [],
      error: error instanceof Error ? error.message : "Failed to load data",
    };
  }
}

function buildFilterString(
  filters: CoolingRegimePriorityFilters,
): string | undefined {
  const conditions: string[] = [];

  if (filters.priorityId) {
    conditions.push(`coolingRegimePriorityId eq ${filters.priorityId}`);
  }

  if (filters.description) {
    conditions.push(`contains(description, '${filters.description}')`);
  }

  if (filters.vbnChangeDate) {
    conditions.push(`vbnChangeDateTime ge ${filters.vbnChangeDate}`);
  }

  if (filters.modifiedDate) {
    conditions.push(`lastModified ge ${filters.modifiedDate}`);
  }

  if (filters.modifiedBy) {
    conditions.push(`contains(lastModifiedBy, '${filters.modifiedBy}')`);
  }

  return conditions.length > 0 ? conditions.join(" and ") : undefined;
}

/**
 * Fetch all cooling regime priorities for dropdown options
 */
export async function fetchAllCoolingRegimePriorities(): Promise<
  CoolingRegimePriorityDto[]
> {
  try {
    const queryConfig = {
      queryParameters: {
        orderby: "coolingRegimePriorityId asc",
      },
    };

    const response = await regularApiClient.api
      .vWithVersion("1")
      .coolingRegimePriorities.get(queryConfig);

    return Array.isArray(response) ? response : [];
  } catch (error) {
    console.error("Failed to fetch cooling regime priorities:", error);
    return [];
  }
}

/**
 * Create a new cooling regime priority
 */
export async function createCoolingRegimePriority(
  description: string,
  createdBy: string = "System",
): Promise<CoolingRegimePriorityDto> {
  const trimmed = (description ?? "").trim();
  if (trimmed.length > 50) {
    throw new Error("Description must be at most 50 characters");
  }
  const now = new Date();
  const response = await regularApiClient.api
    .vWithVersion("1")
    .coolingRegimePriorities.post({
      description: trimmed,
      createdBy,
      created: now,
      lastModifiedBy: createdBy,
      lastModified: now,
    });

  if (!response) {
    throw new Error("Failed to create cooling regime priority");
  }

  return response;
}

/**
 * Update an existing cooling regime priority
 */
export async function updateCoolingRegimePriority(
  id: number,
  description: string,
  createdBy: string,
  created: Date | string | null,
  lastModifiedBy: string = "System",
): Promise<CoolingRegimePriorityDto> {
  const trimmed = (description ?? "").trim();
  if (trimmed.length > 50) {
    throw new Error("Description must be at most 50 characters");
  }
  let createdDate: Date;

  if (!created) {
    createdDate = new Date();
  } else if (typeof created === "string") {
    createdDate = new Date(created);
  } else if (created instanceof Date) {
    createdDate = created;
  } else {
    createdDate = new Date(created);
  }

  await regularApiClient.api
    .vWithVersion("1")
    .coolingRegimePriorities.byId(id)
    .put({
      coolingRegimePriorityId: id,
      description: trimmed,
      createdBy,
      created: createdDate,
      lastModifiedBy,
      lastModified: new Date(),
    });

  const response = await regularApiClient.api
    .vWithVersion("1")
    .coolingRegimePriorities.byId(id)
    .get();

  if (!response) {
    throw new Error("Failed to fetch updated cooling regime priority");
  }

  return response;
}

/**
 * Delete a cooling regime priority
 */
export async function deleteCoolingRegimePriority(id: number): Promise<void> {
  await regularApiClient.api
    .vWithVersion("1")
    .coolingRegimePriorities.byId(id)
    .delete();
}
